/**
 * The liner-note label (designer handoff — "Vinyl Covers").
 *
 * A floating Paper White card that reads as a record-sleeve liner credit, not
 * a blog author card. It sits at the left of the jacket face, vertically
 * centred, and HUGS its content (fit-content width, auto height — never a
 * fixed box; an explicit design correction). The generative pattern shows
 * around it; the crescent record shows past the face's right edge.
 *
 * Top→bottom: format tag (side + medium) · title · hairline · note body with
 * one highlighter mark · hairline · the NOTE BY credit (moon emblem + curator
 * signature). SAVED FROM is deferred to future reposting work, so the credit
 * carries the note's author alone.
 */

import type { CSSProperties } from "react"

import { Avatar } from "@/components/ui/avatar"
import type { PlayableItem, PlayableKind } from "@/lib/player/playable-item"
import type { Playlist } from "@/types"

import { splitTitle } from "./display-title"
import { HairlineRule } from "./jacket-chrome"
import { JacketNotes } from "./jacket-notes"
import { formatTag, jacketTitle, signature } from "./type-voices"

/** Medium word per kind for the format tag (rendered uppercase). */
const MEDIUM: Record<PlayableKind, string> = {
  video: "film",
  audio: "audio",
  photo: "still",
  article: "print",
}

/**
 * The record-catalog tag: side letter + per-side track number + medium —
 * "B1 · PRINT". Side A is the first half of the playlist, B the rest, the
 * number restarting per side the way a record's label does (same split as
 * CatalogMark, rendered full-size uppercase here).
 */
function catalogTag(index: number, total: number, kind: PlayableKind): string {
  const half = Math.ceil(total / 2)
  const side = index < half ? "A" : "B"
  const number = index < half ? index + 1 : index - half + 1
  return `${side}${number} · ${MEDIUM[kind].toUpperCase()}`
}

export function LinerCard({
  playlist,
  item,
  index,
  total,
  reduce,
}: {
  playlist: Playlist
  item: PlayableItem
  index: number
  total: number
  reduce: boolean
}) {
  const curator = playlist.user
  const avatarUrl =
    curator && typeof curator.avatar_url === "string"
      ? curator.avatar_url
      : undefined

  return (
    <div
      data-component="liner-card"
      // Floated at the left, vertically centred, hugging its content. cqi units
      // resolve against the @container jacket face (SleeveCard), so the card
      // widens toward full-bleed on the phone square (min(704px, 90cqi)) and
      // caps at the comp's 704px on desktop; maxHeight + the fold-cut keep a
      // long note inside the square.
      className="pointer-events-none absolute z-[1] flex flex-col"
      style={{
        left: "5cqi",
        top: "50%",
        transform: "translateY(-50%)",
        width: "fit-content",
        maxWidth: "min(704px, 90cqi)",
        maxHeight: "calc(100% - 8cqi)",
      }}
    >
      <div
        className="bg-surface-elevated flex min-h-0 flex-col overflow-hidden rounded-[4px]"
        style={{
          padding: "clamp(18px, 5cqi, 56px)",
          boxShadow: "0 26px 54px rgba(2, 10, 30, 0.24)",
        }}
      >
        <div className="hidden shrink-0 md:block" style={formatTag}>
          {catalogTag(index, total, item.kind)}
        </div>

        <h2
          data-component="liner-title"
          // No top gap on mobile — the tag above is hidden there, so the card
          // padding alone sets the header inset; the gap returns under the tag
          // on desktop.
          className="line-clamp-4 shrink-0 md:mt-4"
          // line-clamp overflow-hidden clips at the box edge, so the tight
          // 0.96 leading eats descenders (y, g, p) on the last line — a hair
          // of paddingBottom drops the clip below the baseline.
          style={{
            ...jacketTitle,
            fontSize: LINER_TITLE_FS,
            paddingBottom: "0.16em",
          }}
        >
          {splitTitle(item.title).head}
        </h2>

        {item.notes && (
          <>
            <HairlineRule className="mt-3 mb-4 shrink-0 md:mt-5 md:mb-6" />
            <div className="jacket-fold-cut min-h-0 shrink">
              <JacketNotes
                notes={item.notes}
                reduce={reduce}
                emphasis="stroke"
                className="gap-[1lh]"
                style={LINER_NOTE_STYLE}
              />
            </div>
          </>
        )}

        {curator && (
          <div className="mt-auto hidden shrink-0 items-center gap-2 pt-3 md:flex">
            <Avatar size="xxs" name={curator.name} avatarUrl={avatarUrl} />
            <div style={signature}>{curator.name}</div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Liner title runs larger than the sleeve-face ramp — the comp's 72px,
 *  scaled to the floating card via cqi with a phone floor and desktop cap. */
const LINER_TITLE_FS = "clamp(26px, 6.5cqi, 60px)"

/** The note's reading voice on the liner card — the comp's 25px/1.5, with a
 *  fixed reading measure so the card hugs width while height flexes. */
const LINER_NOTE_STYLE: CSSProperties = {
  fontSize: "clamp(14px, 2cqi, 19px)",
  lineHeight: 1.5,
  maxWidth: "min(552px, 74cqi)",
}

/**
 * The sleeve's opening seam — a 16px navy gradient at the jacket face's inner
 * right edge (the comp's spine shadow). It slides away with the sleeve on
 * open, so it belongs to the face rather than the slide. Replaces the old 1px
 * mouth-seam hairline.
 */
export function SpineShadow() {
  return (
    <div
      aria-hidden="true"
      data-component="spine-shadow"
      className="pointer-events-none absolute inset-y-0 right-0 w-4"
      style={{
        background:
          "linear-gradient(90deg, rgb(var(--batyr-ink-rgb) / 0.10), rgb(var(--batyr-ink-rgb) / 0))",
      }}
    />
  )
}
